import React, { useState, useEffect } from 'react';
import { message, Modal, Breadcrumb, Button, Select } from 'antd';
import { ExclamationCircleOutlined, DashboardOutlined, PlusSquareOutlined } from '@ant-design/icons';
import StoreDepartmentTable from '../../components/store/StoreDepartmentTable';
import StoreDepartmentModal from '../../components/store/StoreDepartmentModal';
import { useGetAllStoreDepartments, useCreateStoreDepartment, useUpdateStoreDepartment, useDeleteStoreDepartment } from '../../hooks/store/storeDepartmentQuery';
import { useGetAllStores } from '../../hooks/store/storeQuery';
import Header from '../../components/dashboard/header';
import { Link } from 'react-router-dom';
import { MdOutlineInventory2, MdOutlineCategory } from 'react-icons/md';
import { FaWarehouse } from 'react-icons/fa';

const { confirm } = Modal;

const StoreDepartmentPage = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [refreshFlag, setRefreshFlag] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [storeName, setStoreName] = useState('');

  const { loading: storesLoading, stores } = useGetAllStores(refreshFlag);
  const { loading: departmentsLoading, departments } = useGetAllStoreDepartments(selectedStore, refreshFlag);
  const createDepartment = useCreateStoreDepartment();
  const updateDepartment = useUpdateStoreDepartment();
  const deleteDepartment = useDeleteStoreDepartment();

  useEffect(() => {
    if (stores.length > 0 && !selectedStore) {
      setSelectedStore(stores[0].id);
      setStoreName(stores[0].name);
    }
  }, [stores, selectedStore]);

  useEffect(() => {
    if (selectedStore) {
      const store = stores.find(s => s.id === selectedStore);
      if (store) {
        setStoreName(store.name);
      }
    }
  }, [selectedStore, stores]);

  const handleAdd = () => {
    setEditingDepartment(null);
    setModalVisible(true);
  };

  const handleEdit = (record) => {
    setEditingDepartment(record);
    setModalVisible(true);
  };

  const handleDelete = (record) => {
    confirm({
      title: 'Are you sure you want to delete this department?',
      icon: <ExclamationCircleOutlined />,
      content: 'This action cannot be undone.',
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteDepartment(record.id);
          message.success('Department deleted successfully');
          setRefreshFlag(!refreshFlag);
        } catch (error) {
          message.error('Failed to delete department');
        }
      },
    });
  };

  const handleSubmit = async (values) => {
    try {
      if (editingDepartment) {
        await updateDepartment(editingDepartment.id, values);
        message.success('Department updated successfully');
      } else {
        await createDepartment(values);
        message.success('Department created successfully');
      }
      setModalVisible(false);
      setRefreshFlag(!refreshFlag);
    } catch (error) {
      message.error('Failed to save department');
    }
  };

  const { Option } = Select;

  return (
    <>
      <div className="main-container">
        <Header />
        <div className="main-body">
          <div className="info-bar">
            <Breadcrumb>
              <Breadcrumb.Item>
                <DashboardOutlined className="dashboard-icon" />
                <Link to={"/dashboard"}>Dashboard</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <MdOutlineInventory2 />
                <Link to={"/dashboard/store"}>Store</Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>
                <FaWarehouse />
                <Link to="/dashboard/store-department">Store Departments</Link>
              </Breadcrumb.Item>
            </Breadcrumb>
            <div style={{ display: 'flex', gap: '1em', alignItems: 'center' }}>
              <Select
                placeholder="Select Store"
                style={{ width: 200 }}
                value={selectedStore}
                onChange={(value) => setSelectedStore(value)}
                loading={storesLoading}
              >
                {stores.map(store => (
                  <Option key={store.id} value={store.id}>{store.name}</Option>
                ))}
              </Select>
              <Button
                type="primary"
                style={{ backgroundColor: "green", color: "#FFFFFF" }}
                onClick={handleAdd}
                icon={<PlusSquareOutlined />}
              >
                Create Department
              </Button>
            </div>
          </div>
          <div className="table-section">
            <StoreDepartmentTable
              departments={departments}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onAdd={handleAdd}
              loading={departmentsLoading}
              storeName={storeName}
            />
          </div>
        </div>
      </div>
      <StoreDepartmentModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onSubmit={handleSubmit}
        initialValues={editingDepartment}
        title={editingDepartment ? 'Edit Department' : 'Add Department'}
        stores={stores}
      />
    </>
  );
};

export default StoreDepartmentPage;
